"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, ChevronLeft } from "lucide-react";
import { cn, formatPrice } from "@/lib/utils";
import { DEFAULT_CURRENCY } from "@/lib/currencies";
import { useRouter, usePathname } from "next/navigation";
import { DockSheet } from "@/components/ui/dock-sheet";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useModalContainer } from "@/hooks/use-modal-container";
import CartButton from "./cart-button";
import { CartContent } from "./cart-content";
// CartCheckoutBar removed - checkout is now per-seller in SellerGroupComponent
import { CheckoutForm, isShippingFormComplete } from "./checkout-form";
import { OrderPlaced } from "@/components/checkout/order-placed";
import { CountdownTimer } from "./countdown-timer";
import { CHECKOUT_COUNTDOWN_SECONDS } from "@/lib/config";
import { useCartContext } from "@/components/cart/cart-provider";
import { useCartCalculations } from "@/hooks/use-cart-calculations";
import PayPalCheckout, { SellerDetail } from "./paypal-checkout";
import { buildSellerDetail } from "@/lib/payments/seller-detail";
import type { ShippingFormData } from "./shipping-form";
import { clearShippingDraft } from "./shipping-form";
import type { CartItemData, SellerCheckoutInfo, SellerGroup } from "./types";
import {
  buildCheckoutGroupKey,
  groupItemsForCheckout,
  type CheckoutGroupIdentity,
} from "@/lib/cart/checkout-groups";
import { resolveStorageUrl } from "@/lib/storage/public-url";
import { isCountryBannedForShipping as isPayPalCountryBanned } from "@/lib/paypal-countries";
import { buildCheckoutPayload } from "@/lib/checkout-payload";
import { buildCheckoutIntent } from "@/lib/payments/build-checkout-intent";
import { deriveShippingMode } from "@/lib/checkout/derive-shipping-mode";
import { aggregateParcel } from "@/lib/checkout/aggregate-parcel";
import { getOrderByPaymentId, type EnrichedOrder } from "@/lib/adapters/orders";
import { releaseCart, releaseCartViaBeacon } from "@/lib/cart-quick-checkout";
import { linkOrderToMessage } from "@/lib/chat/link-order";
import { reserveCart } from "@/lib/cart/cart-reserve";
import { OutOfStockModal, type ReserveErrorCode } from "./out-of-stock-modal";
import { usePreCheckoutValidation } from "@/hooks/use-pre-checkout-validation";
import { useTranslations } from "next-intl";
import { useOverlay } from "@/components/context/overlay-context";
import { Z } from "@/lib/overlay/z-scale";

type ViewState = "cart" | "checkout" | "confirmation";

/**
 * `SellerGroup` plus the two PayPal-gating fields the cart carries on the
 * seller object. Was an inline intersection repeated at three spots.
 */
type SellerGroupWithRails = SellerGroup & {
  seller: { paypalMerchantId?: string; paypalCapabilities?: string[] };
};

interface CartProps {
  userId: string | null;
  profile?: { id?: string; email?: string; phone?: string } | null;
  hideButton?: boolean;
}

export default function Cart({ userId, profile, hideButton = false }: CartProps) {
  const t = useTranslations("cart");
  const router = useRouter();
  const pathname = usePathname() ?? "/";
  const safeCallback = ["/login", "/register"].includes(pathname) ? "/" : pathname;

  const isMobile = useMediaQuery("(max-width:768px)");
  const { mounted } = useModalContainer(); // 🎯 OPTIMIZATION: Use shared hook instead of duplicate state

  // Overlay-driven visibility (migrated 2026-07-23, spec §E step 1.3 — was
  // local `isOpen` useState + window CustomEvents `open-cart`/
  // `open-cart-force`/`open-cart-checkout`/`close-cart`). The utility slot
  // IS this cart's open/closed state now; `intent`/`forceRefresh` on the
  // payload (lib/overlay/overlay-state.ts) tell the open effect below HOW
  // to open. See that effect for the former openCart/openCartForce/
  // openCartAndCheckout bodies.
  const { overlay, openUtility, closeUtility } = useOverlay();
  const isOpen = overlay.utility.kind === "cart";

  // 🎯 PERFORMANCE FIX: Removed unused viewport states (viewportHeight, isSafari, isChrome, visualViewportHeight)
  // These were declared but never used, causing unnecessary re-renders

  // Loader à l'ouverture
  const [isLoadingCart, setIsLoadingCart] = useState(false);

  // Vue actuelle (panier / checkout / confirmation)
  const [viewState, setViewState] = useState<ViewState>("cart");
  const [placedOrder, setPlacedOrder] = useState<EnrichedOrder | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const [activeSwipeItemId, setActiveSwipeItemId] = useState<string | null>(null);

  // Données du panier via hook
  const { cartItems, loadCart, updateQuantity, updateShippingMethod, removeItem, isInitialized, clearLocalCart, refreshListingShippingMethods } =
    useCartContext();
  
  // État pour la disponibilité des items
  const [availability, setAvailability] = useState<Record<string, number>>({});
  const [availabilityRefreshKey, setAvailabilityRefreshKey] = useState(0);
  
  // Désactivation globale des contrôles pendant l'init ou le chargement forcé
  const controlsDisabled = !isInitialized || isLoadingCart;

  const {
    uniqueItemCount,
    subtotal,
    shippingTotal,
    serviceFee,
    total,
    allShippingMethodsSelected,
    hasUnavailableItems,
    currencies,
    byCurrency,
    bySeller,
    hasMultipleCurrencies,
  } = useCartCalculations(cartItems, availability);

  // Ref for bySeller to avoid stale closures in event listeners
  const bySellerRef = React.useRef(bySeller);
  bySellerRef.current = bySeller;

  // Per-group checkout state. Identity is seller × currency × shipping mode
  // — the shipping-mode axis is load-bearing, not decorative: one seller can
  // have both `legacy` and `live_rate` listings in the cart, and those are two
  // separate checkouts (the server rejects a mixed intent outright).
  // @see lib/cart/checkout-groups.ts
  const [checkoutSeller, setCheckoutSeller] = useState<CheckoutGroupIdentity | null>(null);
  const checkoutSellerKey = checkoutSeller ? buildCheckoutGroupKey(checkoutSeller) : null;
  const [reservedListingIds, setReservedListingIds] = useState<string[]>([]);

  // Seller validation (shared hook)
  const {
    sellerValidationErrors,
    validate: validateSellers,
    clearError: clearSellerValidationError,
  } = usePreCheckoutValidation();

  // Render-side twin of `useCartCalculations().bySeller`: the SAME canonical
  // partition (seller × currency × shipping mode), so the card the buyer sees
  // and the totals/Pay-button it carries are the same checkout by
  // construction. Keyed by the group key, NOT by seller id — a seller with
  // both `legacy` and `live_rate` listings renders two cards, and two cards
  // sharing one React key would be a silent reconciliation bug.
  // @see lib/cart/checkout-groups.ts
  const groupItemsBySeller = useCallback((items: CartItemData[]) => {
    const acc: Record<string, SellerGroupWithRails> = {};

    for (const group of groupItemsForCheckout(items)) {
      const firstItem = group.items[0];
      acc[group.key] = {
        seller: {
          // Still the SELLER id: per-seller concerns (validation errors, the
          // rail check) are not per-checkout-group.
          id: group.sellerId,
          name: firstItem.sellerName || "Unknown Seller",
          // Resolve here (producer) so every consumer of the group — the
          // avatar <Image> in seller-group.tsx included — gets an absolute
          // URL; a bare storage path crashes next/image's URL constructor.
          //
          // `resolveStorageUrl` rather than `getAvatarUrl`: `sellerAvatarUrl`
          // is a `RawImageRef` (branded, deliberately NOT assignable to
          // `string`) and this funnel takes the brand natively, whereas
          // `getAvatarUrl`'s signature still predates it. Same call the mobile
          // twin makes for this exact field (apps/mobile/lib/cart-groups.ts).
          // The `any[]` parameter this callback used to take is what let the
          // brand slip through unchecked here.
          profileImage: resolveStorageUrl(firstItem.sellerAvatarUrl) ?? "",
          checkoutNote:
            group.items.map((i) => i.sellerCheckoutNote?.trim()).find(Boolean) || null,
          paypalMerchantId: firstItem.paypalMerchantId ?? undefined,
          paypalCapabilities: firstItem.paypalCapabilities ?? [],
        },
        items: group.items,
        checkoutGroup: {
          key: group.key,
          sellerId: group.sellerId,
          currencyCode: group.currencyCode,
          shippingMode: group.shippingMode,
        },
      };
    }

    return acc;
  }, []);

  // Regroupe par seller pour PayPal
  const itemsBySeller = groupItemsBySeller(cartItems);

  const singleCurrencyCode = currencies.length === 1 ? currencies[0] : DEFAULT_CURRENCY
  const currencyCheckoutOptions = React.useMemo(
    () =>
      currencies.map((currencyCode) => {
        const group = byCurrency[currencyCode]
        const canCheckoutForCurrency =
          Boolean(group) && group.subtotal > 0 && group.allShippingMethodsSelected && !group.hasUnavailableItems
        return { currencyCode, total: group?.total ?? 0, canCheckout: canCheckoutForCurrency }
      }),
    [byCurrency, currencies],
  )

  // Track reservation state
  const [reservationsActive, setReservationsActive] = useState(false);
  // Per-seller reservation creation tracking (sellerKey or null)
  const [creatingReservationsFor, setCreatingReservationsFor] = useState<string | null>(null);
  const [oosModal, setOosModal] = useState<{ open: boolean; titles: string[]; errorCode?: ReserveErrorCode }>(() => ({ open: false, titles: [] }));

  // Open effect — reacts to the utility slot becoming `{kind:"cart", ...}`.
  // Replaces the old openCart/openCartForce/openCartAndCheckout imperative
  // functions (each was a plain window-event handler, called once per
  // dispatch regardless of whether the cart was already open). Deliberately
  // keyed ONLY on `overlay.utility` (not exhaustive-deps) — `openUtility`
  // (lib/overlay/overlay-state.ts) always produces a fresh object per
  // dispatch, even for a logically-identical payload, so this still re-runs
  // once per producer call — including a re-click while already open —
  // matching the pre-migration semantics. It must NOT also re-run on every
  // `cartItems`/`loadCart` change, which `overlay.utility` does not
  // correlate with.
  useEffect(() => {
    const utility = overlay.utility;
    if (utility.kind !== "cart") return;

    const runOpen = async () => {
      if (utility.forceRefresh) {
        // Was `open-cart-force` / openCartForce ("See in Cart" right after
        // Add to Cart). Always bypasses the 60s `cart_last_load` cache —
        // the caller needs the row it just added, not a stale snapshot —
        // and skips the availabilityRefreshKey bump (see the plain branch
        // below): the add-to-cart mutation already synced availability, so
        // a second forced check is redundant. 2s timeout budget (vs 1.5s
        // plain) — openCartForce's original choice, preserved as-is.
        setIsLoadingCart(true);
        try {
          await Promise.race([
            loadCart(),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error("Cart load timeout")), 2000),
            ),
          ]);
          localStorage.setItem("cart_last_load", Date.now().toString());
        } catch (error) {
          console.warn("Cart force load timeout or error:", error instanceof Error ? error.message : "Unknown error");
        } finally {
          setIsLoadingCart(false);
        }
      } else {
        // Was plain `open-cart` / openCart. Always force a fresh
        // availability fetch when opening the cart (reservations may have
        // expired since last fetch — no realtime event for expiry), but
        // skip the `loadCart()` round-trip entirely if a recent (<60s)
        // load is already cached — instant open from cache.
        setAvailabilityRefreshKey((k) => k + 1);
        const lastLoad = localStorage.getItem("cart_last_load");
        const now = Date.now();
        if (lastLoad && now - parseInt(lastLoad) < 60000 && cartItems.length > 0) {
          setIsLoadingCart(false);
          return;
        }
        setIsLoadingCart(true);
        try {
          await Promise.race([
            loadCart(),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error("Cart load timeout")), 1500),
            ),
          ]);
          localStorage.setItem("cart_last_load", now.toString());
        } catch (error) {
          console.warn("Cart load timeout or error:", error instanceof Error ? error.message : "Unknown error");
        } finally {
          setIsLoadingCart(false);
        }
      }

      // Absorbs the former `open-cart-checkout` event / openCartAndCheckout
      // (dead listener pre-migration, spec §C.1 UtilityOverlay note) — once
      // cart data has settled, auto-select the seller and jump straight to
      // checkout IFF there's exactly one seller in the cart (the "Buy Now"
      // single-item shape this exists for). Multi-seller carts land on the
      // cart view so the buyer picks which seller to check out. Uses
      // `bySellerRef` (not `bySeller` directly) — same stale-closure
      // avoidance as before, now doubly relevant since this effect is
      // intentionally not re-run on every `bySeller` change.
      if (utility.intent === "checkout") {
        setTimeout(() => {
          const currentBySeller = bySellerRef.current;
          const sellerKeys = Object.keys(currentBySeller);
          if (sellerKeys.length === 1) {
            const calc = currentBySeller[sellerKeys[0]];
            gotoCheckout({
              sellerId: calc.sellerId,
              currencyCode: calc.currencyCode,
              shippingMode: calc.shippingMode,
            });
          }
        }, 0);
      }
    };

    runOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlay.utility]);

  // Close/cleanup effect — fires once on every true→false transition of
  // `isOpen`, regardless of WHY it closed: our own close (X button,
  // backdrop, login/register redirect, order-placed CTA — all now just
  // call `closeUtility()`), an external `closeUtility()` call (e.g.
  // paypal-checkout's post-payment teardown, cart-item's card-tap
  // navigation), or another overlay taking the utility slot out from under
  // the cart (§C.1 policy). Centralizing cleanup here — instead of inline
  // at every close call site, as pre-migration `closeCart()` did — means
  // every path gets it for free with one implementation.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (!wasOpen || isOpen) return; // only true -> false transitions

    if (reservationsActive && userId) {
      setReservationsActive(false);
      releaseCart({
        listingIds: reservedListingIds.length > 0 ? reservedListingIds : cartItems.map((i) => i.listingId),
      }).catch((error) => console.warn("Failed to release reservations:", error));
    }
    setViewState("cart");
    setPlacedOrder(null);
    setIsProcessing(false);
    setActiveSwipeItemId(null);
    setCheckoutSeller(null);
    setReservedListingIds([]);
  }, [isOpen, reservationsActive, userId, reservedListingIds, cartItems]);

  const handleLogin = () => {
    closeUtility();
    router.push(`/login?callbackUrl=${encodeURIComponent(safeCallback)}`);
  };
  const handleRegister = () => {
    closeUtility();
    router.push(`/register?callbackUrl=${encodeURIComponent(safeCallback)}`);
  };

  // Auto-open the drawer when a previous page asked for it (e.g. recovery
  // success page → /feed). The flag is one-shot and tab-scoped. Mount-only
  // — `openUtility` is stable (useReducer dispatch never changes identity).
  useEffect(() => {
    try {
      if (sessionStorage.getItem("kraahl:auto-open-cart") === "1") {
        sessionStorage.removeItem("kraahl:auto-open-cart");
        openUtility({ kind: "cart" });
      }
    } catch {}
  }, [openUtility]);

  const placedOrderNumber = placedOrder?.order_number ?? null;

  useEffect(() => {
    if (viewState !== "confirmation") return
    if (!placedOrderNumber) return
    const key = "kraahl:chat_buy_now_context"
    const raw = localStorage.getItem(key)
    if (!raw) return
    let parsed: any = null
    try {
      parsed = JSON.parse(raw)
    } catch {
      localStorage.removeItem(key)
      return
    }
    const roomName = typeof parsed?.roomName === "string" ? parsed.roomName : ""
    const messageId = typeof parsed?.messageId === "string" ? parsed.messageId : ""
    if (!roomName || !messageId) {
      localStorage.removeItem(key)
      return
    }
    localStorage.removeItem(key)
    linkOrderToMessage({ roomName, messageId, orderNumber: placedOrderNumber }).catch(() => {})
  }, [placedOrderNumber, viewState])

  // No more scroll manipulation - let the browser handle everything naturally
  // No need to manipulate body scroll - let the bottom sheet handle it naturally
  // The bottom sheet should open over the current content without affecting scroll position

  // No special viewport handling - let browser handle it naturally

  // 🎯 PERFORMANCE FIX: Memoize listingIds to avoid unstable dependency
  const cartListingIds = React.useMemo(
    () => cartItems.map(i => i.listingId).join(','),
    [cartItems]
  );

  // Best-effort: release on tab close/refresh
  useEffect(() => {
    if (!userId) return;
    const onUnload = () => {
      if (!reservationsActive) return;
      try {
        const listingIds = reservedListingIds.length > 0 ? reservedListingIds : cartItems.map(i => i.listingId)
        releaseCartViaBeacon({ listingIds })
      } catch {}
    };
    const onVisibility = () => { if (document.visibilityState === 'hidden') onUnload(); };
    window.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onUnload);
    window.addEventListener('beforeunload', onUnload);
    return () => {
      window.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onUnload);
      window.removeEventListener('beforeunload', onUnload);
    };
  }, [reservationsActive, userId, cartListingIds, cartItems, reservedListingIds]);
  const gotoCheckout = async (group: CheckoutGroupIdentity) => {
    if (!userId) {
      alert(t("mustBeLoggedIn"));
      return
    }

    // Find the checkout-group calculation by key
    const sellerKey = buildCheckoutGroupKey(group);
    const sellerCalc = bySeller[sellerKey];
    if (!sellerCalc) {
      console.error('[Cart] Seller calculation not found:', sellerKey);
      return;
    }

    setCheckoutSeller(group);

    const targetCartItems = sellerCalc.items;
    const groupAllShippingSelected = sellerCalc.allShippingMethodsSelected;
    const groupHasUnavailable = sellerCalc.hasUnavailableItems;

    if (!groupAllShippingSelected) {
      alert(t("selectShippingMethod"))
      return
    }

    if (groupHasUnavailable) {
      const titles = targetCartItems
        .filter((item) => {
          const stock = availability[item.listingId] ?? item.maxQuantity
          const isOutOfStock = item.quantity > stock
          // Rail-aware: seller is unavailable iff no payment rail accepts checkout.
          // Mirrors cart-item.tsx; do not gate on paypalMerchantId.
          const isUnavailable = (item.availableProviders?.length ?? 0) === 0
          // WS4.6 follow-up: live_rate items have no fixed methods by design.
          const hasNoShippingMethods = item.shippingMode !== "live_rate" && (item.shippingMethods?.length ?? 0) === 0
          const isCancelled = Boolean((item as any).chatPrivateCancelledAt)
          return isOutOfStock || isUnavailable || hasNoShippingMethods || isCancelled
        })
        .map((item) => item.name)
      setOosModal({
        open: true,
        titles: titles.length > 0 ? titles : targetCartItems.map((i) => i.name).slice(0, 5),
      })
      return
    }

    // Rail-aware pre-checkout gate. Map to the narrow SellerAvailabilityCheck
    // shape so rail-availability is the explicit, type-checked input.
    const validationResult = await validateSellers(
      targetCartItems.map((i) => ({
        sellerId: i.sellerId,
        availableProviders: i.availableProviders ?? [],
        name: i.name,
      })),
    )
    if (!validationResult.valid) {
      setOosModal({
        open: true,
        titles: validationResult.error!.titles,
        errorCode: validationResult.error!.errorCode,
      })
      return
    }

    if (!reservationsActive) {
      setCreatingReservationsFor(sellerKey)
      try {
        const availableSet = new Set<string>()
        targetCartItems.forEach((i) => {
          const stock = availability[i.listingId] ?? i.maxQuantity
          if (i.quantity <= stock) availableSet.add(i.listingId)
        })
        const unavailableTitles = targetCartItems.filter((i) => !availableSet.has(i.listingId)).map((i) => i.name)

        if (unavailableTitles.length > 0) {
          setOosModal({ open: true, titles: unavailableTitles })
        }

        const items = targetCartItems
          .filter((item) => availableSet.has(item.listingId))
          .map((item) => ({ listingId: item.listingId, quantity: item.quantity }))

        if (items.length === 0) {
          setCreatingReservationsFor(null)
          return
        }

        const result = await reserveCart({ items })

        if (result.ok) {
          setReservationsActive(true)
          setReservedListingIds(items.map((i) => i.listingId))
          setViewState("checkout")
        } else {
          const failedTitles = targetCartItems.map((i) => i.name).slice(0, 5)
          setOosModal({ open: true, titles: failedTitles, errorCode: result.errorCode })
          try {
            const params = new URLSearchParams()
            targetCartItems.forEach((i) => params.append("listingId", i.listingId))
            await fetch(`/api/cart/availability?${params.toString()}`)
          } catch {}
        }
      } catch (error) {
        console.error("Failed to create reservations:", error)
        alert(t("reservationFailed"))
      } finally {
        setCreatingReservationsFor(null)
      }
    } else {
      setViewState("checkout")
    }
  }
  const backToCart = () => {
    setViewState("cart");
    setPlacedOrder(null);
    setActiveSwipeItemId(null);
    // Release reservations when going back to cart
    if (reservationsActive && userId) {
      setReservationsActive(false);
      releaseCart({
        listingIds: reservedListingIds.length > 0 ? reservedListingIds : cartItems.map(i => i.listingId),
      }).catch(error => console.warn("Failed to release reservations:", error));
    }
    setCheckoutSeller(null);
    setReservedListingIds([]);
    clearSellerValidationError();
  };

  // buildOrderPayload moved to lib/checkout-payload.ts as buildCheckoutPayload

  // Envoi de la commande
  const completeOrder = async (data: ShippingFormData, paypalData?: { orderID: string; captureID?: string; captureIds?: Record<string, string>; orderNumber?: string; orderId?: string }, shouldSaveAddress?: boolean) => {
    if (!userId) {
      alert(t("logInToOrder"));
      setIsProcessing(false);
      return;
    }

    // Per-seller checkout requires checkoutSeller to be set
    if (!checkoutSeller) {
      console.error('[Cart] completeOrder called without checkoutSeller - this should not happen');
      alert(t("checkoutNoSeller"));
      setIsProcessing(false);
      return;
    }

    const sellerKey = buildCheckoutGroupKey(checkoutSeller)
    const orderSellerCalc = bySeller[sellerKey]
    if (!orderSellerCalc) {
      console.error('[Cart] Seller calculation not found in completeOrder:', sellerKey);
      alert(t("checkoutNoData"));
      setIsProcessing(false);
      return;
    }

    if (shouldSaveAddress) {
      try {
        const res = await fetch("/api/address-book", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            label: data.address,
            first_name: data.firstName,
            last_name: data.lastName,
            address: data.address,
            apartment: data.apartment,
            city: data.city,
            state: data.state,
            postal: data.postalCode,
            country: data.country,
            phone: data.phoneCountryCode && data.phoneNumber
              ? `${data.phoneCountryCode}${data.phoneNumber}`
              : "",
            email: data.email,
            is_main: false,
          }),
        });
        if (!res.ok) {
          console.warn("Address not saved:", await res.json());
        }
      } catch (error) {
        console.warn("Address not saved:", error);
      }
    }
    setIsProcessing(true);

    // Order must be created server-side by the capture route
    if (paypalData?.orderNumber) {
      console.log('[CART] Order created server-side:', paypalData.orderNumber);
      setPlacedOrder(null);
      setViewState("confirmation");
      try {
        const order = await getOrderByPaymentId(paypalData.orderID);
        if (!order) {
          throw new Error(`Order not found for PayPal order ${paypalData.orderID}`);
        }
        setPlacedOrder(order);
      } catch (error) {
        console.error('[CART] Failed to hydrate placed order:', error);
        router.push("/orders-placed");
      } finally {
        setIsProcessing(false);
        if (!hasMultipleCurrencies) {
          clearLocalCart();
        } else {
          loadCart(true);
        }
        clearShippingDraft();
      }
      return;
    }

    // Capture succeeded but order wasn't created server-side — reconciliation job will handle it
    console.warn('[CART] Payment captured but orderNumber missing — reconciliation will create the order. PayPal order:', paypalData?.orderID);
    setIsProcessing(false);
    if (!hasMultipleCurrencies) {
      clearLocalCart();
    } else {
      loadCart(true);
    }
    clearShippingDraft();
    // Don't show confirmation with a fake order number — just go to orders page
    router.push("/orders-placed");
  };

  // Handle partial payment - create order for completed items, keep failed in cart
  // Annule un swipe si on clique en dehors
  const handleCartClick = (e: React.MouseEvent) => {
    if (!activeSwipeItemId) return;
    const target = e.target as HTMLElement;
    const stillOnItem = target.closest(`.cart-item-${activeSwipeItemId}`);
    const onDeleteBtn = target.closest(`.delete-button-${activeSwipeItemId}`);
    if (!stillOnItem && !onDeleteBtn) {
      setActiveSwipeItemId(null);
    }
  };

  // Fonction pour vérifier si le pays sélectionné est banni par les méthodes de livraison
  const isCountryBannedForCartShipping = (countryCode: string): boolean => {
    if (!countryCode) return false;

    const sellerCalc = checkoutSellerKey ? bySeller[checkoutSellerKey] : null;
    const itemsToCheck = sellerCalc ? sellerCalc.items : cartItems;

    return itemsToCheck.some(item => {
      // Si aucune méthode de livraison n'est sélectionnée, on vérifie toutes les méthodes disponibles
      if (!item.selectedShippingMethod) {
        // Si toutes les méthodes disponibles bannissent ce pays, alors le pays est banni
        return item.shippingMethods.length > 0 &&
               item.shippingMethods.every(method =>
                 isPayPalCountryBanned(countryCode, method.bannedCountries)
               );
      }

      // Si une méthode est sélectionnée, on vérifie si elle bannit ce pays
      return isPayPalCountryBanned(countryCode, item.selectedShippingMethod.bannedCountries);
    });
  };

  // Build per-checkout-group info from bySeller calculations
  const sellerCheckoutInfo = React.useMemo(() => {
    const info: Record<string, SellerCheckoutInfo> = {};

    Object.entries(bySeller).forEach(([key, calc]) => {
      info[key] = {
        groupKey: calc.groupKey,
        sellerId: calc.sellerId,
        currencyCode: calc.currencyCode,
        shippingMode: calc.shippingMode,
        subtotal: calc.subtotal,
        shippingTotal: calc.shippingTotal,
        serviceFee: calc.serviceFee,
        total: calc.total,
        canCheckout: calc.allShippingMethodsSelected && !calc.hasUnavailableItems && calc.subtotal > 0,
        allShippingMethodsSelected: calc.allShippingMethodsSelected,
      };
    });

    return info;
  }, [bySeller]);

  function renderInnerContent() {
  if (isLoadingCart) {
    return (
      <div className="flex h-[50vh] items-center justify-center" onClick={handleCartClick}>
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-200 border-t-gray-700" />
      </div>
    )
  }

  if (isMobile) {
    if (viewState === "checkout") {
      // Per-group checkout: get the active group's calculation
      const checkoutSellerCalc = checkoutSellerKey ? bySeller[checkoutSellerKey] : null;
      const activeCurrencyCode = checkoutSeller?.currencyCode || singleCurrencyCode;
      const checkoutItems = checkoutSellerCalc ? checkoutSellerCalc.items : cartItems;
      const checkoutSubtotal = checkoutSellerCalc ? checkoutSellerCalc.subtotal : subtotal;
      const checkoutShippingTotal = checkoutSellerCalc ? checkoutSellerCalc.shippingTotal : shippingTotal;
      const checkoutServiceFee = checkoutSellerCalc ? checkoutSellerCalc.serviceFee : serviceFee;
      const checkoutTotal = checkoutSellerCalc ? checkoutSellerCalc.total : total;
      const checkoutAllShippingSelected = checkoutSellerCalc ? checkoutSellerCalc.allShippingMethodsSelected : allShippingMethodsSelected;
      const checkoutHasUnavailable = checkoutSellerCalc ? checkoutSellerCalc.hasUnavailableItems : hasUnavailableItems;
      const checkoutSellerNote =
        checkoutItems.map((i) => i.sellerCheckoutNote?.trim()).find(Boolean) ?? null;
      const checkoutPreorderItems = checkoutItems
        .filter((i) => i.listingType === "preorder")
        .map((i) => ({
          listingId: i.listingId,
          title: i.name,
          estimated_delivery: i.estimatedDelivery ?? null,
          min_orders: i.minOrders ?? null,
          preorder_status: i.preorderStatus ?? null,
        }));

      // Single seller checkout - only one sellerDetails entry
      const sellerDetails: SellerDetail[] = checkoutSellerCalc
        ? [buildSellerDetail(checkoutSellerCalc)]
        : [];

      // Debug logging (mobile)
      console.log("[CHECKOUT-MOBILE] Items debug:", {
        checkoutSeller,
        cartItemsCount: cartItems.length,
        checkoutItemsCount: checkoutItems.length,
        checkoutItems: checkoutItems.map(i => ({ name: i.name, sellerId: i.sellerId, currency: i.currency })),
        sellerDetailsCount: sellerDetails.length,
      });

      return (
        <div className="h-full">
          {isProcessing ? (
            <div className="flex h-full flex-col items-center justify-center text-foreground">
              <Loader2 className="mb-4 h-10 w-10 animate-spin text-muted-foreground" />
              <span className="text-lg font-medium">{t("processing")}</span>
            </div>
          ) : (
            <CheckoutForm
              className="bg-sheet text-foreground"
              embedded
              hideShippingAddressTitle
              sellerCheckoutNote={checkoutSellerNote}
              preorderItems={checkoutPreorderItems}
              subtotal={checkoutSubtotal}
              shippingTotal={checkoutShippingTotal}
              serviceFee={checkoutServiceFee}
              total={checkoutTotal}
              currencyCode={activeCurrencyCode}
              onCompleteAction={(data, saveAddr) => {
                setIsProcessing(true)
                completeOrder(data, undefined, saveAddr)
              }}
              profile={profile}
              userId={userId}
              renderPaymentAction={(formData, selectedCountry, ctx) => {
                // WS4.5 — per-seller Shippo routing for the mobile cart
                // drawer. Single-seller invariant (PLAN §J.5) holds:
                // `checkoutItems` is the active seller-group's items going
                // to ONE PayPalCheckout. Plain (not `useMemo`) — file
                // memoizes other derivations but `renderPaymentAction` is
                // itself a JSX-prop callback, so wrapping in useMemo would
                // need a stable callback identity which this prop doesn't
                // demand. See .tondocs/skills/hex.md §1 — pure-domain reads
                // forwarded to the delivery shell.
                const shippingMode = deriveShippingMode(checkoutItems)
                const parcel = shippingMode === "live_rate"
                  ? aggregateParcel(
                      checkoutItems.map((i) => ({
                        quantity: i.quantity,
                        parcelWeight: i.parcelWeight,
                        parcelLength: i.parcelLength,
                        parcelWidth: i.parcelWidth,
                        parcelHeight: i.parcelHeight,
                        massUnit: i.parcelMassUnit,
                        distanceUnit: i.parcelDistanceUnit,
                      })),
                    ) ?? undefined
                  : undefined
                return (
                <PayPalCheckout
                  intent={formData && checkoutSeller ? buildCheckoutIntent(
                    checkoutItems,
                    formData,
                    activeCurrencyCode,
                    Object.fromEntries(checkoutItems.map((i) => [i.id, { kind: "listing" as const }])),
                    checkoutSeller.sellerId,
                  ) : null}
                  totalAmount={ctx.effectiveTotal}
                  sellerDetails={sellerDetails}
                  formData={formData}
                  selectedCountry={selectedCountry}
                  currencyCode={activeCurrencyCode}
                  disabled={checkoutHasUnavailable || !checkoutAllShippingSelected || !isShippingFormComplete(formData) || (formData ? isCountryBannedForCartShipping(formData.country) : false)}
                  isCountryBannedForShipping={isCountryBannedForCartShipping}
                  shippingMode={shippingMode}
                  parcel={parcel}
                  onLiveRateAmountChange={ctx.onLiveRateAmountChange}
                  getCheckoutPayload={(extras) => {
                    if (!formData || !userId) return null
                    return buildCheckoutPayload(
                      userId,
                      checkoutItems,
                      formData,
                      activeCurrencyCode,
                      { subtotal: checkoutSubtotal, shippingTotal: checkoutShippingTotal, serviceFee: checkoutServiceFee, total: checkoutTotal },
                      undefined,
                      extras?.externalShipping ?? null,
                    )
                  }}
                  onApproveAction={(paypalData) => {
                    if (!formData) {
                      alert(t("fillShippingInfo"))
                      return
                    }
                    setIsProcessing(true)
                    completeOrder(formData, paypalData)
                  }}
                />
                )
              }}
            />
          )}
        </div>
      )
    }

    if (viewState === "confirmation") {
      if (!placedOrder) {
        return (
          <div className="flex h-full flex-col items-center justify-center bg-sheet text-foreground">
            <Loader2 className="mb-4 h-10 w-10 animate-spin text-muted-foreground" />
            <span className="text-lg font-medium">{t("processing")}</span>
          </div>
        )
      }

      return (
        <OrderPlaced
          order={placedOrder}
          onContinue={() => {
            closeUtility()
            router.push("/orders-placed")
          }}
          onContact={() => {
            closeUtility()
            setTimeout(() => router.push("/contact"), 200)
          }}
        />
      )
    }

    return (
      <div className="h-full flex flex-col" onClick={handleCartClick}>
        <div className="flex-1 overflow-y-auto overflow-x-hidden pb-4">
          {hasMultipleCurrencies ? (
            <div className="space-y-8 pb-4">
              {currencyCheckoutOptions.map((opt) => {
                const group = byCurrency[opt.currencyCode];
                if (!group || !Array.isArray(group.items) || group.items.length === 0) return null;
                const itemsBySellerForCurrency = groupItemsBySeller(group.items);
                return (
                  <section key={opt.currencyCode} className="space-y-3">
                    <div className="flex items-center justify-between text-foreground text-sm font-semibold pb-2 border-b border-sheet-handle/60 px-4">
                      <span>{opt.currencyCode}</span>
                      <span>{formatPrice(opt.total ?? 0, opt.currencyCode)}</span>
                    </div>
                    <CartContent
                      cartItems={group.items}
                      isMobile
                      activeSwipeItemId={activeSwipeItemId}
                      setActiveSwipeItemIdAction={setActiveSwipeItemId}
                      updateQuantityAction={updateQuantity}
                      removeItemAction={(id) => {
                        removeItem(id)
                        setActiveSwipeItemId(null)
                      }}
                      updateShippingMethodAction={updateShippingMethod}
                      refreshListingShippingMethodsAction={refreshListingShippingMethods}
                      itemsBySeller={itemsBySellerForCurrency}
                      userId={userId}
                      onAvailabilityChange={setAvailability}
                      controlsDisabled={controlsDisabled}
                      subtotal={group.subtotal}
                      shippingTotal={group.shippingTotal}
                      serviceFee={group.serviceFee}
                      sellerValidationErrors={sellerValidationErrors}
                      availabilityRefreshKey={availabilityRefreshKey}
                      sellerCheckoutInfo={sellerCheckoutInfo}
                      onSellerCheckout={gotoCheckout}
                      creatingReservationsFor={creatingReservationsFor}
                      onLogin={handleLogin}
                      onRegister={handleRegister}
                    />
                  </section>
                );
              })}
            </div>
          ) : (
            <CartContent
              cartItems={cartItems}
              isMobile
              activeSwipeItemId={activeSwipeItemId}
              setActiveSwipeItemIdAction={setActiveSwipeItemId}
              updateQuantityAction={updateQuantity}
              removeItemAction={(id) => {
                removeItem(id)
                setActiveSwipeItemId(null)
              }}
              updateShippingMethodAction={updateShippingMethod}
              refreshListingShippingMethodsAction={refreshListingShippingMethods}
              itemsBySeller={itemsBySeller}
              userId={userId}
              onAvailabilityChange={setAvailability}
              controlsDisabled={controlsDisabled}
              subtotal={subtotal}
              shippingTotal={shippingTotal}
              serviceFee={serviceFee}
              sellerValidationErrors={sellerValidationErrors}
              availabilityRefreshKey={availabilityRefreshKey}
              sellerCheckoutInfo={sellerCheckoutInfo}
              onSellerCheckout={gotoCheckout}
              creatingReservationsFor={creatingReservationsFor}
              onLogin={handleLogin}
              onRegister={handleRegister}
            />
          )}
        </div>
      </div>
    )
  }

  if (viewState === "cart") {
    return (
      <div className="flex flex-col h-full bg-card text-foreground">
        <div className="px-6 pt-6 border-b">
          <div className="flex items-start justify-between relative z-10">
            <button
              className="inline-flex h-10 w-10 items-center justify-center rounded-full opacity-0 pointer-events-none p-3 -m-3"
              aria-label="Placeholder"
              disabled
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={closeUtility}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full hover:bg-muted transition-colors p-3 -m-3"
              aria-label={t("closeCart")}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="mt-0 text-center -translate-y-6 relative z-0">
            <h2 className="text-2xl font-semibold text-foreground">{t("title", { count: cartItems.length })}</h2>
          </div>
        </div>
        <div className="flex-1 pt-2 overflow-y-auto overflow-x-hidden pb-4" onClick={handleCartClick}>
          {hasMultipleCurrencies ? (
            <div className="space-y-8 pb-4">
              {currencyCheckoutOptions.map((opt) => {
                const group = byCurrency[opt.currencyCode];
                if (!group || !Array.isArray(group.items) || group.items.length === 0) return null;
                const itemsBySellerForCurrency = groupItemsBySeller(group.items);
                return (
                  <section key={opt.currencyCode} className="space-y-3">
                    <div className="flex items-center justify-between text-sm font-semibold pb-2 border-b border-border/50 px-4">
                      <span>{opt.currencyCode}</span>
                      <span>{formatPrice(opt.total ?? 0, opt.currencyCode)}</span>
                    </div>
                    <CartContent
                      cartItems={group.items}
                      isMobile={false}
                      compact
                      activeSwipeItemId={activeSwipeItemId}
                      setActiveSwipeItemIdAction={setActiveSwipeItemId}
                      updateQuantityAction={updateQuantity}
                      removeItemAction={(id) => {
                        removeItem(id)
                        setActiveSwipeItemId(null)
                      }}
                      updateShippingMethodAction={updateShippingMethod}
                      refreshListingShippingMethodsAction={refreshListingShippingMethods}
                      itemsBySeller={itemsBySellerForCurrency}
                      userId={userId}
                      onAvailabilityChange={setAvailability}
                      controlsDisabled={controlsDisabled}
                      subtotal={group.subtotal}
                      shippingTotal={group.shippingTotal}
                      serviceFee={group.serviceFee}
                      sellerValidationErrors={sellerValidationErrors}
                      availabilityRefreshKey={availabilityRefreshKey}
                      sellerCheckoutInfo={sellerCheckoutInfo}
                      onSellerCheckout={gotoCheckout}
                      creatingReservationsFor={creatingReservationsFor}
                      onLogin={handleLogin}
                      onRegister={handleRegister}
                    />
                  </section>
                );
              })}
            </div>
          ) : (
            <CartContent
              cartItems={cartItems}
              isMobile={false}
              compact
              activeSwipeItemId={activeSwipeItemId}
              setActiveSwipeItemIdAction={setActiveSwipeItemId}
              updateQuantityAction={updateQuantity}
              removeItemAction={(id) => {
                removeItem(id)
                setActiveSwipeItemId(null)
              }}
              updateShippingMethodAction={updateShippingMethod}
              refreshListingShippingMethodsAction={refreshListingShippingMethods}
              itemsBySeller={itemsBySeller}
              userId={userId}
              onAvailabilityChange={setAvailability}
              controlsDisabled={controlsDisabled}
              subtotal={subtotal}
              shippingTotal={shippingTotal}
              serviceFee={serviceFee}
              sellerValidationErrors={sellerValidationErrors}
              availabilityRefreshKey={availabilityRefreshKey}
              sellerCheckoutInfo={sellerCheckoutInfo}
              onSellerCheckout={gotoCheckout}
              creatingReservationsFor={creatingReservationsFor}
              onLogin={handleLogin}
              onRegister={handleRegister}
            />
          )}
        </div>
      </div>
    )
  }

  if (viewState === "checkout") {
    // Per-group checkout: get the active group's calculation
    const checkoutSellerCalc = checkoutSellerKey ? bySeller[checkoutSellerKey] : null;
    const activeCurrencyCode = checkoutSeller?.currencyCode || singleCurrencyCode;
    const checkoutItems = checkoutSellerCalc ? checkoutSellerCalc.items : cartItems;
    const checkoutSubtotal = checkoutSellerCalc ? checkoutSellerCalc.subtotal : subtotal;
    const checkoutShippingTotal = checkoutSellerCalc ? checkoutSellerCalc.shippingTotal : shippingTotal;
    const checkoutServiceFee = checkoutSellerCalc ? checkoutSellerCalc.serviceFee : serviceFee;
    const checkoutTotal = checkoutSellerCalc ? checkoutSellerCalc.total : total;
    const checkoutAllShippingSelected = checkoutSellerCalc ? checkoutSellerCalc.allShippingMethodsSelected : allShippingMethodsSelected;
    const checkoutHasUnavailable = checkoutSellerCalc ? checkoutSellerCalc.hasUnavailableItems : hasUnavailableItems;
    const checkoutSellerNoteDesktop =
      checkoutItems.map((i) => i.sellerCheckoutNote?.trim()).find(Boolean) ?? null;
    const checkoutPreorderItemsDesktop = checkoutItems
      .filter((i) => i.listingType === "preorder")
      .map((i) => ({
        listingId: i.listingId,
        title: i.name,
        estimated_delivery: i.estimatedDelivery ?? null,
        min_orders: i.minOrders ?? null,
        preorder_status: i.preorderStatus ?? null,
      }));

    // Single seller checkout - only one sellerDetails entry
    const sellerDetails: SellerDetail[] = checkoutSellerCalc
      ? [buildSellerDetail(checkoutSellerCalc)]
      : [];

    // Debug logging
    console.log("[CHECKOUT] Items debug:", {
      checkoutSeller,
      cartItemsCount: cartItems.length,
      checkoutItemsCount: checkoutItems.length,
      checkoutItems: checkoutItems.map(i => ({ name: i.name, sellerId: i.sellerId, paypalMerchantId: i.paypalMerchantId })),
      sellerDetailsCount: sellerDetails.length,
      sellerDetails: sellerDetails.map(s => ({ sellerId: s.sellerId, paypalMerchantId: s.paypalMerchantId })),
    });

    return (
      <div className="flex flex-col h-full bg-card text-foreground">
        {/* 🎯 Desktop header for checkout - same structure as cart */}
        <div className="px-6 pt-6 border-b">
          <div className="flex items-start justify-between relative z-10">
            <button
              onClick={backToCart}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full hover:bg-muted transition-colors p-3 -m-3"
              aria-label={t("goBack")}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={closeUtility}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full hover:bg-muted transition-colors p-3 -m-3"
              aria-label={t("closeCart")}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="mt-0 text-center -translate-y-6 relative z-0">
            <h2 className="text-2xl font-semibold text-foreground">{t("checkout")}</h2>
          </div>
          <div className="-mx-6">
            <CountdownTimer
              initialSeconds={CHECKOUT_COUNTDOWN_SECONDS}
              onExpire={backToCart}
            />
          </div>
        </div>
        
        {/* 🎯 Checkout content with proper scrolling - no footer like mobile */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden pb-6">
          {isProcessing ? (
            <div className="flex h-[60vh] flex-col items-center justify-center">
              <Loader2 className="mb-4 h-10 w-10 animate-spin text-muted-foreground" />
              <span className="text-lg font-medium">{t("processing")}</span>
            </div>
          ) : (
            <CheckoutForm
            sellerCheckoutNote={checkoutSellerNoteDesktop}
            preorderItems={checkoutPreorderItemsDesktop}
            subtotal={checkoutSubtotal}
            shippingTotal={checkoutShippingTotal}
            serviceFee={checkoutServiceFee}
            total={checkoutTotal}
            currencyCode={activeCurrencyCode}
            onCompleteAction={(data, saveAddr) => {
              setIsProcessing(true)
              completeOrder(data, undefined, saveAddr)
            }}
            profile={profile}
            userId={userId}
            renderPaymentAction={(formData, selectedCountry, ctx) => {
              // WS4.5 — per-seller Shippo routing for the desktop full-page
              // cart. Same `checkoutItems` source as the mobile callsite
              // above; single-seller invariant (PLAN §J.5) holds.
              const shippingMode = deriveShippingMode(checkoutItems)
              const parcel = shippingMode === "live_rate"
                ? aggregateParcel(
                    checkoutItems.map((i) => ({
                      quantity: i.quantity,
                      parcelWeight: i.parcelWeight,
                      parcelLength: i.parcelLength,
                      parcelWidth: i.parcelWidth,
                      parcelHeight: i.parcelHeight,
                      massUnit: i.parcelMassUnit,
                      distanceUnit: i.parcelDistanceUnit,
                    })),
                  ) ?? undefined
                : undefined
              return (
              <PayPalCheckout
                intent={formData && checkoutSeller ? buildCheckoutIntent(
                    checkoutItems,
                    formData,
                    activeCurrencyCode,
                    Object.fromEntries(checkoutItems.map((i) => [i.id, { kind: "listing" as const }])),
                    checkoutSeller.sellerId,
                  ) : null}
                totalAmount={ctx.effectiveTotal}
                sellerDetails={sellerDetails}
                formData={formData}
                selectedCountry={selectedCountry}
                currencyCode={activeCurrencyCode}
                disabled={checkoutHasUnavailable || !checkoutAllShippingSelected || (formData ? isCountryBannedForCartShipping(formData.country) : false)}
                isCountryBannedForShipping={isCountryBannedForCartShipping}
                shippingMode={shippingMode}
                parcel={parcel}
                onLiveRateAmountChange={ctx.onLiveRateAmountChange}
                getCheckoutPayload={(extras) => {
                  if (!formData || !userId) return null
                  return buildCheckoutPayload(
                    userId,
                    checkoutItems,
                    formData,
                    activeCurrencyCode,
                    { subtotal: checkoutSubtotal, shippingTotal: checkoutShippingTotal, serviceFee: checkoutServiceFee, total: checkoutTotal },
                    undefined,
                    extras?.externalShipping ?? null,
                  )
                }}
                onApproveAction={(paypalData) => {
                  if (!formData) {
                    alert(t("fillShippingInfo"))
                    return
                  }
                  setIsProcessing(true)
                  completeOrder(formData, paypalData)
                }}
              />
              )
            }}
          />
        )}
        </div>
      </div>
    )
  }

  if (viewState === "confirmation") {
    return (
      <div className="flex flex-col h-full bg-sheet text-foreground">
        <div className="px-6 pt-6 border-b border-border shrink-0">
          <div className="flex items-start justify-between relative z-10">
            <div className="h-10 w-10" />
            <button
              onClick={closeUtility}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full hover:bg-muted transition-colors p-3 -m-3"
              aria-label={t("closeCart")}
            >
              <X className="h-5 w-5 text-foreground" />
            </button>
          </div>
          <div className="mt-0 text-center -translate-y-6 relative z-0">
            <h2 className="text-2xl font-semibold text-foreground">{t("orderConfirmed")}</h2>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {placedOrder ? (
            <OrderPlaced
              order={placedOrder}
              onContinue={() => {
                closeUtility()
                router.push("/orders-placed")
              }}
              onContact={() => {
                closeUtility()
                setTimeout(() => router.push("/contact"), 200)
              }}
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center">
              <Loader2 className="mb-4 h-10 w-10 animate-spin text-muted-foreground" />
              <span className="text-lg font-medium">{t("processing")}</span>
            </div>
          )}
        </div>
      </div>
    )
  }

  return null
}

  const bodyContent = renderInnerContent()

  // Per-seller checkout: no global footer needed (checkout is per seller group)
  // No more snap points needed - using fixed height like ChatTabSlider

  // Chrome per viewState. The sheet primitive owns handle / close / title /
  // back; only what is SPECIFIC to each state lives here.
  const sheetTitle =
    viewState === "checkout"
      ? t("checkout")
      : viewState === "confirmation"
        ? t("orderConfirmed")
        : t("title", { count: cartItems.length });

  const panel = isMobile ? (
        <>
          <DockSheet
            open={isOpen}
            onOpenChange={closeUtility}
            title={sheetTitle}
            onBack={viewState === "checkout" ? backToCart : undefined}
            // The countdown is a full-bleed bar (`border-t`/`border-b` across
            // the sheet), so it cancels the `belowTitle` slot's own insets.
            belowTitle={
              viewState === "checkout" ? (
                <CountdownTimer
                  className="-mx-4 -mb-3 -mt-1"
                  initialSeconds={CHECKOUT_COUNTDOWN_SECONDS}
                  onExpire={backToCart}
                />
              ) : undefined
            }
            height="90dvh"
            contentClassName="px-0 pt-0"
          >
            <div className="flex-1 overflow-y-auto pb-4">
              {bodyContent}
            </div>
          </DockSheet>
        </>
  ) : (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50"
          style={{ zIndex: Z.overlayNested }}
          onClick={closeUtility}
        />
      )}
      <div
        // e2e observability: the drawer is always mounted and slides via
        // transform, so Playwright's :visible cannot tell open from closed
        // (a translated-off-screen box still counts as visible) — tests key
        // on data-open instead. @see tests/e2e/cart-helpers.ts
        data-testid="cart-drawer"
        data-open={isOpen ? "true" : "false"}
        className={cn(
          // Hand-rolled desktop drawer — NOT a `DialogCard`, but it renders the
          // very same `bodyContent` as the mobile `DockSheet` below, on the same
          // theme tokens. THEME-FOLLOWING since Phase 2 step 2f: its own forced
          // `dark` was the fifth of the five flip points (see
          // `SHEET_PANEL_DARK`'s header for the list).
          // @see .tondocs/ui/HANDOFF_SHEET_LIGHT_THEME_2026_08_01.md §4.1
          "fixed top-0 right-0 h-full w-[400px] transition-transform bg-card",
          isOpen ? "translate-x-0" : "translate-x-full"
        )}
        // Same tier as the backdrop above (utility slot, §C.3) — DOM order
        // (backdrop div renders first) resolves the stacking tie so the
        // panel still paints on top, same visual result as the old
        // z-55/z-60 pair without needing a second tier.
        style={{ zIndex: Z.overlayNested }}
        onClick={handleCartClick}
      >
        {bodyContent}
      </div>
    </>
  );

  // No viewport detection - let browser handle it naturally

  // Hover preload — starts loading cart data 500ms into a hover over the
  // cart button, so the drawer feels instant when actually clicked. Was a
  // `document.querySelector('[data-cart-button]')` + manual mouseenter/
  // mouseleave listener pair; `data-cart-button` is gone (it was also the
  // click-forwarding target nav components used, now replaced by direct
  // `openUtility` calls — spec §E step 1.3), so CartButton takes the hover
  // handlers as plain props instead of being found via the DOM.
  const cartHoverPreloadTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleCartButtonHoverStart = () => {
    cartHoverPreloadTimeout.current = setTimeout(() => {
      if (!isOpen && cartItems.length === 0) {
        loadCart();
      }
    }, 500);
  };
  const handleCartButtonHoverEnd = () => {
    if (cartHoverPreloadTimeout.current) clearTimeout(cartHoverPreloadTimeout.current);
  };

  return (
    <>
      {!hideButton && (
        <CartButton
          itemCount={uniqueItemCount}
          onClick={() => openUtility({ kind: "cart" })}
          onMouseEnter={handleCartButtonHoverStart}
          onMouseLeave={handleCartButtonHoverEnd}
        />
      )}
      <OutOfStockModal open={oosModal.open} listingTitles={oosModal.titles} errorCode={oosModal.errorCode} onCloseAction={() => setOosModal({ open: false, titles: [] })} />
      {mounted && createPortal(panel, document.body)}
    </>
  );
}
